<script setup>
import {reactive} from 'vue';
import {useAlerts, useErrors, useOfetch} from '../composables';
import Alert from './Alert';
import FormGroup from './FormGroup';

const {__, _x} = wp.i18n;

const {action, nonce} = defineProps({
    action: {
        type: String,
        required: true,
    },
    nonce: {
        type: String,
        required: true,
    },
});

const {isFetching, execute} = useOfetch();
const errors = useErrors();
const {alerts, flash, clear: clearAlerts} = useAlerts();
const formData = reactive({
    name: '',
    organization: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '',
});

async function send() {
    clearAlerts();
    errors.clear();
    try {
        const {message} = await execute(window.IsaL.adminUrl, {
            method: 'POST',
            query: {action},
            body: new URLSearchParams([
                ['name', formData.name],
                ['organization', formData.organization],
                ['email', formData.email],
                ['subject', formData.subject],
                ['message', formData.message],
                ['__validate_email', formData.honeypot],
                ['nonce', nonce],
            ]),
        });
        reset();
        flash(message);
    } catch ({statusCode, data}) {
        if (statusCode === 422) {
            errors.record(data.errors);
            flash(__('Certains champs requièrent votre attention.', 'isa-l'), 'error', false);
        } else if (data) {
            data.errors.forEach(error => flash(error, 'error'));
        } else {
            flash(__('Une erreur est survenue.', 'isa-l'), 'error', false);
        }
    }
}

function reset() {
    Object.assign(formData, {
        name: '',
        organization: '',
        email: '',
        subject: '',
        message: '',
        honeypot: '',
    });
    errors.clear();
}
</script>

<template>
    <form @submit.prevent="send()" ref="form" novalidate>
        <div class="mb-4 space-y-1">
            <Alert v-for="alert in alerts"
                   :message="alert.message"
                   :level="alert.level"
                   :key="alert.message"/>
        </div>

        <input type="text" name="__validate_email" class="hidden" v-model="formData.honeypot">

        <div class="space-y-4">
            <form-group :errors="errors.get('name')">
                <label for="name" class="block mb-1">{{ __('Nom *', 'isa-l') }}</label>
                <input type="text"
                       id="name"
                       name="name"
                       class="w-full px-3 py-1.5 rounded bg-white text-black"
                       :placeholder="_x('Michel Dupont', 'Contact form name placeholder', 'isa-l')"
                       required
                       v-model="formData.name">
            </form-group>

            <form-group :errors="errors.get('organization')">
                <label for="organization" class="block mb-1">{{ __('Organisation', 'isa-l') }}</label>
                <input type="text"
                       id="organization"
                       name="organization"
                       :placeholder="_x('Société', 'Contact form organization placeholder', 'isa-l')"
                       autocomplete="organization"
                       class="w-full px-3 py-1.5 rounded bg-white text-black"
                       v-model="formData.organization">
            </form-group>

            <form-group :errors="errors.get('email')">
                <label for="email" class="block mb-1">{{ __('Email *', 'isa-l') }}</label>
                <input type="email"
                       id="email"
                       name="email"
                       :placeholder="_x('email@exemple.com', 'Contact form email placeholder', 'isa-l')"
                       class="w-full px-3 py-1.5 rounded bg-white text-black"
                       required
                       v-model="formData.email">
            </form-group>

            <form-group>
                <label for="subject" class="block mb-1">{{ __('Objet', 'isa-l') }}</label>
                <input type="text"
                       id="subject"
                       name="subject"
                       :placeholder="_x('Objet de votre message', 'Contact form subject placeholder', 'isa-l')"
                       class="w-full px-3 py-1.5 rounded bg-white text-black"
                       v-model="formData.subject">
            </form-group>

            <form-group :errors="errors.get('message')">
                <label for="message" class="block mb-1">{{ __('Message *', 'isa-l') }}</label>
                <textarea id="message"
                          name="message"
                          :placeholder="_x('Saisissez votre message', 'Contact form message placeholder', 'isa-l')"
                          class="w-full px-3 py-1.5 rounded bg-white text-black"
                          rows="4"
                          required
                          v-model="formData.message"></textarea>
            </form-group>

            <div class="text-right">
                <p class="mb-4 text-sm">* Ce champ est obligatoire.</p>
                <button type="submit"
                        class="inline-flex items-center px-6 py-2 bg-black hover:bg-white text-white hover:text-black transition-colors rounded"
                        :disabled="isFetching">
                    {{ isFetching ? __('Envoi...', 'isa-l') : __('Envoyer', 'isa-l') }}
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24" class="ml-1 -mr-1">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                              d="M4.75 19.25L12 4.75L19.25 19.25L12 15.75L4.75 19.25Z"></path>
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                              d="M12 15.5V12.75"></path>
                    </svg>
                </button>
            </div>
        </div>
    </form>
</template>
